import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import { CheckCircle2, Clock, XCircle, AlertTriangle, Shield, Wrench, RefreshCw, Users, Mail, Search } from 'lucide-react';
import { toast } from 'sonner';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

const KpiCard = ({ icon: Icon, label, value, color, testid }) => (
  <Card className="bg-zinc-900 border-zinc-800" data-testid={testid}>
    <CardContent className="p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">{label}</div>
        <div className="text-2xl font-bold text-white">{value}</div>
      </div>
    </CardContent>
  </Card>
);

const StatusBadge = ({ entry }) => {
  if (entry.is_subscribed && (entry.subscription_status || '').toLowerCase() === 'active') {
    return <Badge className="bg-emerald-600/20 text-emerald-300 border-emerald-500/40" data-testid="badge-active">Actif</Badge>;
  }
  if ((entry.subscription_status || '').toLowerCase() === 'trialing') {
    if ((entry.trial_days_left ?? 0) > 0) {
      return <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40" data-testid="badge-trial">Essai {entry.trial_days_left}j</Badge>;
    }
    return <Badge className="bg-orange-600/20 text-orange-300 border-orange-500/40" data-testid="badge-trial-expired">Essai expiré</Badge>;
  }
  if (['canceled', 'cancelled', 'unpaid', 'past_due', 'incomplete_expired'].includes((entry.subscription_status || '').toLowerCase())) {
    return <Badge className="bg-red-600/20 text-red-300 border-red-500/40" data-testid="badge-cancelled">Annulé</Badge>;
  }
  if (entry.role === 'artisan') {
    return <Badge className="bg-blue-600/20 text-blue-300 border-blue-500/40">Artisan</Badge>;
  }
  if (entry.role === 'admin' || entry.workspace_id === 'legacy-main') {
    return <Badge className="bg-purple-600/20 text-purple-300 border-purple-500/40">Admin</Badge>;
  }
  return <Badge className="bg-zinc-700 text-zinc-300">—</Badge>;
};

const SubscriberRow = ({ entry }) => (
  <div className="flex items-start justify-between gap-3 p-4 bg-zinc-800/40 hover:bg-zinc-800/70 rounded-lg border border-zinc-800 transition-colors" data-testid={`subscriber-row-${entry.id}`}>
    <div className="flex items-start gap-3 min-w-0 flex-1">
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center flex-shrink-0 mt-0.5">
        <span className="text-sm font-semibold text-zinc-950">
          {(entry.name || entry.email || '?').charAt(0).toUpperCase()}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-white truncate" data-testid={`subscriber-name-${entry.id}`}>
            {entry.name || <span className="italic text-zinc-500">Sans nom</span>}
          </p>
          {entry.email_verified === false && entry.role !== 'artisan' && entry.role !== 'admin' && (
            <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-400 px-1.5 py-0">Email non vérifié</Badge>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-sm text-zinc-400 mt-0.5">
          <Mail className="w-3 h-3" />
          <span className="truncate" data-testid={`subscriber-email-${entry.id}`}>{entry.email}</span>
        </div>
        {entry.company_name && (
          <p className="text-xs text-zinc-500 mt-0.5 truncate">🏢 {entry.company_name}</p>
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-zinc-500">
          <span>Inscrit : <strong className="text-zinc-400">{fmtDate(entry.created_at)}</strong></span>
          {entry.trial_ends_at && <span>Fin essai : <strong className="text-zinc-400">{fmtDate(entry.trial_ends_at)}</strong></span>}
          {entry.subscribed_at && <span>Abonné depuis : <strong className="text-emerald-300">{fmtDate(entry.subscribed_at)}</strong></span>}
          {entry.cancelled_at && <span>Annulé le : <strong className="text-red-300">{fmtDate(entry.cancelled_at)}</strong></span>}
          {entry.stripe_customer_id && <span>Stripe : <code className="text-zinc-400">{entry.stripe_customer_id.slice(0, 12)}…</code></span>}
        </div>
      </div>
    </div>
    <div className="flex-shrink-0">
      <StatusBadge entry={entry} />
    </div>
  </div>
);

const EmptyState = ({ label }) => (
  <div className="text-center py-12 text-zinc-500 text-sm" data-testid="empty-state">
    Aucun utilisateur dans cette catégorie pour le moment.
    <p className="text-xs text-zinc-600 mt-1">{label}</p>
  </div>
);

export const AdminSubscribersPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/subscribers`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filterEntries = (entries) => {
    if (!search.trim()) return entries;
    const q = search.toLowerCase();
    return entries.filter(e =>
      (e.email || '').toLowerCase().includes(q) ||
      (e.name || '').toLowerCase().includes(q) ||
      (e.company_name || '').toLowerCase().includes(q)
    );
  };

  if (loading && !data) {
    return (
      <div className="space-y-6" data-testid="admin-subscribers-loading">
        <div className="h-10 w-72 bg-zinc-800 rounded animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-24 bg-zinc-800 rounded-lg animate-pulse" />)}
        </div>
      </div>
    );
  }

  const counts = data?.counts || {};

  return (
    <div className="space-y-6" data-testid="admin-subscribers-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-2">
            <Users className="w-7 h-7 text-amber-400" />
            Abonnés
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Vue complète de tous les comptes : abonnés payants, essais en cours, et annulés.
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" className="border-zinc-700 text-zinc-300 hover:text-white" data-testid="refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Rafraîchir
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={CheckCircle2}
          label="Abonnés payants"
          value={counts.subscribed || 0}
          color="bg-emerald-600"
          testid="kpi-subscribed"
        />
        <KpiCard
          icon={Clock}
          label="Essais en cours"
          value={counts.trial_active || 0}
          color="bg-amber-500"
          testid="kpi-trial-active"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Essais expirés"
          value={counts.trial_expired || 0}
          color="bg-orange-600"
          testid="kpi-trial-expired"
        />
        <KpiCard
          icon={XCircle}
          label="Annulés"
          value={counts.cancelled || 0}
          color="bg-red-600"
          testid="kpi-cancelled"
        />
      </div>

      {/* Recherche */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher par email, nom ou société…"
          className="pl-9 bg-zinc-900 border-zinc-800 text-white"
          data-testid="search-subscribers-input"
        />
      </div>

      {/* Onglets */}
      <Tabs defaultValue="subscribed" className="w-full">
        <TabsList className="bg-zinc-900 border border-zinc-800 flex-wrap h-auto">
          <TabsTrigger value="subscribed" data-testid="tab-subscribed">
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Payants ({counts.subscribed || 0})
          </TabsTrigger>
          <TabsTrigger value="trial_active" data-testid="tab-trial-active">
            <Clock className="w-4 h-4 mr-1.5" /> Essais ({counts.trial_active || 0})
          </TabsTrigger>
          <TabsTrigger value="trial_expired" data-testid="tab-trial-expired">
            <AlertTriangle className="w-4 h-4 mr-1.5" /> Expirés ({counts.trial_expired || 0})
          </TabsTrigger>
          <TabsTrigger value="cancelled" data-testid="tab-cancelled">
            <XCircle className="w-4 h-4 mr-1.5" /> Annulés ({counts.cancelled || 0})
          </TabsTrigger>
          <TabsTrigger value="admin_legacy" data-testid="tab-admin">
            <Shield className="w-4 h-4 mr-1.5" /> Admins ({counts.admin_legacy || 0})
          </TabsTrigger>
          <TabsTrigger value="artisans" data-testid="tab-artisans">
            <Wrench className="w-4 h-4 mr-1.5" /> Artisans ({counts.artisans || 0})
          </TabsTrigger>
        </TabsList>

        {[
          { key: 'subscribed', label: 'Aucun abonné payant pour le moment.' },
          { key: 'trial_active', label: 'Aucun essai gratuit en cours.' },
          { key: 'trial_expired', label: 'Aucun essai expiré.' },
          { key: 'cancelled', label: 'Aucun abonnement annulé.' },
          { key: 'admin_legacy', label: 'Aucun compte administrateur.' },
          { key: 'artisans', label: 'Aucun artisan référencé.' },
        ].map(({ key, label }) => {
          const entries = filterEntries(data?.[key] || []);
          return (
            <TabsContent key={key} value={key} className="mt-4">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white text-base">
                    {entries.length} résultat{entries.length > 1 ? 's' : ''}{search && ` (filtré)`}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {entries.length === 0 ? (
                    <EmptyState label={label} />
                  ) : (
                    <div className="space-y-2">
                      {entries.map(e => <SubscriberRow key={e.id || e.email} entry={e} />)}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
};

export default AdminSubscribersPage;
